// Overview operational metrics (#811) — what the console decides before it renders a number.
//
// Two rules run through all of it. The console never computes a metric: the server owns every
// value, and this file only chooses which of them may be shown and how. And an absent value is
// never a zero: a metric the server returned as `null` renders as an explained dash, because
// "0 %" and "nothing is settled yet" are different claims about the business.
import type { OverviewMetrics, OverviewMetricsQuery, OverviewPeriod } from "../api/types";
import { inboxDateRangeProblem, type InboxDateRangeProblem } from "./screen-inbox.logic";

/** The order the period control offers, from the shortest window to the one the operator types. */
export const OVERVIEW_PERIODS: readonly OverviewPeriod[] = ["day", "week", "month", "custom"];

export const OVERVIEW_DEFAULT_PERIOD: OverviewPeriod = "day";

/** The custom period's own state — both ends, either of which may still be empty. */
export interface OverviewRangeState {
  from: string;
  to: string;
}

export const EMPTY_OVERVIEW_RANGE: OverviewRangeState = { from: "", to: "" };

/**
 * What is wrong with a custom period, or nothing.
 *
 * `incomplete` is this screen's own: the Inbox accepts an open-ended filter, but a metric needs
 * two ends to divide by. The other three are the Inbox's problems by name (#814) and are detected
 * with the Inbox's own function, so the two screens cannot drift into disagreeing about which
 * ranges are typable.
 */
export type OverviewRangeProblem = InboxDateRangeProblem | "incomplete";

export function overviewRangeProblem(
  period: OverviewPeriod,
  range: OverviewRangeState,
): OverviewRangeProblem | null {
  if (period !== "custom") return null;
  const shared = inboxDateRangeProblem(range);
  if (shared) return shared;
  return range.from && range.to ? null : "incomplete";
}

/**
 * The query to send, or `null` when there is nothing sensible to ask.
 *
 * A period the console already knows the server will refuse is not sent. That is not a second
 * validator competing with the server — the server still answers 400 on its own — it is what
 * keeps a half-typed date from showing the operator a failed load instead of a sentence.
 */
export function overviewMetricsQuery(
  period: OverviewPeriod,
  range: OverviewRangeState,
): OverviewMetricsQuery | null {
  if (overviewRangeProblem(period, range)) return null;
  if (period !== "custom") return { period };
  return { period, from: range.from, to: range.to };
}

/**
 * How a single metric should read.
 *
 * `unavailable` is a first-class outcome rather than a formatting accident: the server said it
 * could not compute this honestly, and the console has to pass that on rather than round it to 0.
 */
export type MetricReading =
  | { kind: "loading" }
  | { kind: "value"; text: string; denominator: number | null }
  | { kind: "unavailable"; denominator: number | null };

/** German and English both take a locale here; nothing about a number is guessed from the DOM. */
function locale(lang: string): string {
  return lang === "de" ? "de-DE" : "en-GB";
}

export function formatCount(value: number, lang: string): string {
  return new Intl.NumberFormat(locale(lang)).format(value);
}

export function formatRatio(value: number, lang: string): string {
  // Whole per cent. A response rate quoted to a decimal implies a precision the denominator —
  // often a handful of enquiries — does not have.
  return new Intl.NumberFormat(locale(lang), {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatAverage(value: number, lang: string): string {
  return new Intl.NumberFormat(locale(lang), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

function isNonNegativeFinite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

export function ratioReading(metrics: OverviewMetrics | null, lang: string): MetricReading {
  if (!metrics) return { kind: "loading" };
  const ratio = metrics.processed_within_24h_ratio as unknown;
  const numerator = metrics.processed_within_24h as unknown;
  const denominator = metrics.processed_within_24h_denominator as unknown;
  if (ratio === null) {
    return {
      kind: "unavailable",
      denominator: isNonNegativeInteger(denominator) ? denominator : null,
    };
  }
  if (
    typeof ratio !== "number"
    || !Number.isFinite(ratio)
    || ratio < 0
    || ratio > 1
    || !isNonNegativeInteger(numerator)
    || !isNonNegativeInteger(denominator)
    || denominator === 0
    || numerator > denominator
  ) {
    return { kind: "unavailable", denominator: null };
  }
  return {
    kind: "value",
    text: formatRatio(ratio, lang),
    denominator,
  };
}

export function averageReading(metrics: OverviewMetrics | null, lang: string): MetricReading {
  if (!metrics) return { kind: "loading" };
  const average = metrics.average_guest_count as unknown;
  const denominator = metrics.average_guest_count_denominator as unknown;
  if (average === null) {
    return {
      kind: "unavailable",
      denominator: isNonNegativeInteger(denominator) ? denominator : null,
    };
  }
  if (!isNonNegativeFinite(average) || !isNonNegativeInteger(denominator) || denominator === 0) {
    return { kind: "unavailable", denominator: null };
  }
  return {
    kind: "value",
    text: formatAverage(average, lang),
    denominator,
  };
}

export function countReading(
  metrics: OverviewMetrics | null,
  pick: (m: OverviewMetrics) => unknown,
  lang: string,
): MetricReading {
  if (!metrics) return { kind: "loading" };
  const value = pick(metrics);
  if (!isNonNegativeInteger(value)) return { kind: "unavailable", denominator: null };
  return { kind: "value", text: formatCount(value, lang), denominator: null };
}

/**
 * The panel as a whole.
 *
 * `error` keeps the last answer for the same period, because blanking the panel on a failed
 * refresh loses information the operator already had.
 */
export type OverviewPanelState = "loading" | "problem" | "error" | "empty" | "ready";

export function overviewPanelState(input: {
  metrics: OverviewMetrics | null;
  loading: boolean;
  error: string | null;
  rangeProblem: OverviewRangeProblem | null;
}): OverviewPanelState {
  if (input.rangeProblem) return "problem";
  if (input.metrics === null) return input.error ? "error" : "loading";
  if (input.error) return "error";
  return input.metrics.new_enquiries === 0 ? "empty" : "ready";
}

/** The period as a sentence: "5–11 June 2026", in the business's zone and the operator's language. */
export function periodRangeLabel(metrics: OverviewMetrics | null, lang: string): string {
  if (!metrics) return "";
  const format = (iso: string) => {
    const at = new Date(`${iso}T12:00:00Z`);
    if (!Number.isFinite(at.getTime())) return iso;
    return new Intl.DateTimeFormat(locale(lang), {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(at);
  };
  const from = format(metrics.date_from);
  return metrics.date_from === metrics.date_to ? from : `${from} – ${format(metrics.date_to)}`;
}
